import React, { useEffect, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import IconClose from 'components/icons/close';
import VisuallyHidden from 'components/visually-hidden';

import { Button, SearchBarBoxShadow, SearchBarWrapper, SearchInput } from './styled/search-bar';

const messages = defineMessages({
  yourAddress: {
    id: 'yourAddress',
    defaultMessage: 'Your Address',
    description:
      'A label for an input where the user enters their address (or another address) to search for stores nearby.',
  },
  enterAddressOrDrag: {
    id: 'enterAddressOrDrag',
    defaultMessage: 'Enter an address or drag the map to find stores near you.',
    description:
      'Instruction to users to find stores on the map either by entering an address or dragging the map.',
  },
  clearSearchBar: {
    id: 'clearSearchBar',
    defaultMessage: 'Clear search bar',
    description: 'A note to screen reader users that clicking the icon will clear the search bar.',
  },
});

interface ISearchBarProps {
  autoFocus: boolean;
  onPlaceChanged: (address: string) => void;
  value: string;
  isListView?: boolean;
  smallSize?: boolean;
}

export default ({
  autoFocus,
  onPlaceChanged,
  value,
  isListView = false,
  smallSize = false,
}: ISearchBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus, pathname]);

  return (
    <SearchBarWrapper smallSize={smallSize} isListView={isListView}>
      <SearchBarBoxShadow>
        <SearchInput
          ariaKey="storelocator"
          autoFocus={autoFocus}
          required
          value={value}
          onAddressChange={onPlaceChanged}
          label={formatMessage(messages.yourAddress)}
          aria-label={formatMessage(messages.enterAddressOrDrag)}
          ref={inputRef}
          data-testid="storelocator-autocomplete"
        />
      </SearchBarBoxShadow>
      {!!value.length && (
        <Button
          onClick={() => {
            onPlaceChanged('');
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
          aria-label={formatMessage(messages.clearSearchBar)}
        >
          <IconClose />
          <VisuallyHidden>{formatMessage(messages.clearSearchBar)}</VisuallyHidden>
        </Button>
      )}
    </SearchBarWrapper>
  );
};
